import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, UserPlus, TrendingUp, TrendingDown, LogOut, Edit, Trash2, UserCog, Search, Settings, DollarSign } from 'lucide-react';
import UserDetailManagement from '../components/UserDetailManagement';

const API = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://localhost:8001/api'
  : '/api';

function ManagerPanel({ user }) {
  const navigate = useNavigate();
  // Compute manager info directly from user
  const manager = user ? {
    id: user.id,
    full_name: user.full_name,
    username: user.email.split('@')[0],
    email: user.email
  } : null;
  const [groups, setGroups] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  
  // Modal states
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'info' });
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  
  const showAlert = (message, type = 'info') => setAlertModal({ show: true, message, type });
  const showConfirm = (message, onConfirm) => setConfirmModal({ show: true, message, onConfirm });
  
  // Create group modal
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', price_margin: 0 });
  
  // Add member modal
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  
  // Edit price margin modal
  const [showEditMargin, setShowEditMargin] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newMargin, setNewMargin] = useState(0);
  
  // User Detail Management
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [selectedUserForDetail, setSelectedUserForDetail] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  
  // Grup Varlık Ayarları
  const [showAssetSettings, setShowAssetSettings] = useState(false);
  const [selectedGroupForAssets, setSelectedGroupForAssets] = useState(null);
  const [allAssets, setAllAssets] = useState([]);
  const [assetSearchQuery, setAssetSearchQuery] = useState('');
  const [groupAssetSettings, setGroupAssetSettings] = useState({});
  const [editingAsset, setEditingAsset] = useState(null);
  const [assetSettingForm, setAssetSettingForm] = useState({
    price_adjustment: 0
  });
  
  // Mobile menu
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [memberSearchQuery, setMemberSearchQuery] = useState('');

  const token = localStorage.getItem('token');
  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!user) {
          setLoading(false);
          return;
        }
        
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        
        // Fetch groups
        const groupsRes = await axios.get(`${API}/manager/groups?manager_id=${user.id}`, config);
        setGroups(groupsRes.data);
        
        // Fetch all users - using manager endpoint
        const usersRes = await axios.get(`${API}/manager/users`, config);
        setAllUsers(usersRes.data || []);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching manager data:', error);
        setLoading(false);
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate('/login');
        }
      }
    };

    if (!user) {
      return;
    }
    
    if (!user.is_manager) {
      navigate('/login');
      return;
    }
    
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      if (!user) {
        setLoading(false);
        return;
      }
      
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Fetch groups
      const groupsRes = await axios.get(`${API}/manager/groups?manager_id=${user.id}`, config);
      setGroups(groupsRes.data);
      
      // Fetch all users - using manager endpoint
      const usersRes = await axios.get(`${API}/manager/users`, config);
      setAllUsers(usersRes.data || []);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false); // CRITICAL: Set loading false even on error
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate('/login');
      }
    }
  };

  const handleCreateGroup = async () => {
    try {
      const response = await axios.post(
        `${API}/manager/groups?manager_id=${manager.id}&manager_name=${encodeURIComponent(manager.full_name || '')}`,
        newGroup,
        axiosConfig
      );
      
      // Yeni grubu listeye ekle (anında görünsün)
      if (response.data && response.data.id) {
        setGroups(prev => [...prev, response.data]);
      }
      
      showAlert('Grup oluşturuldu!', 'success');
      setShowCreateGroup(false);
      setNewGroup({ name: '', price_margin: 0 });
      
      // Ayrıca backend'den güncel listeyi al
      await fetchData();
    } catch (error) {
      showAlert('Hata: ' + (error.response?.data?.detail || 'Hata oluştu!'), 'error');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    showConfirm('Bu grubu silmek istediğinizden emin misiniz?', async () => {
      try {
        await axios.delete(`${API}/manager/groups/${groupId}?manager_id=${manager.id}`, axiosConfig);
        showAlert('Grup silindi!', 'success');
        fetchData();
      } catch (error) {
        showAlert('Hata: ' + (error.response?.data?.detail || 'Hata oluştu!'), 'error');
      }
    });
  };

  const handleOpenAddMember = async (group) => {
    setSelectedGroup(group);
    
    // Fetch group members
    try {
      const res = await axios.get(`${API}/manager/groups/${group.id}/members?manager_id=${manager.id}`, axiosConfig);
      setGroupMembers(res.data);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
    
    setShowAddMember(true);
  };

  const handleAddMember = async (userId) => {
    try {
      await axios.post(
        `${API}/manager/groups/${selectedGroup.id}/members?manager_id=${manager.id}`,
        { user_id: userId },
        axiosConfig
      );
      
      showAlert('Üye eklendi!', 'success');
      handleOpenAddMember(selectedGroup); // Refresh members
      fetchData();
    } catch (error) {
      showAlert('Hata: ' + (error.response?.data?.detail || 'Hata oluştu!'), 'error');
    }
  };

  const handleRemoveMember = async (userId) => {
    showConfirm('Bu üyeyi gruptan çıkarmak istediğinizden emin misiniz?', async () => {
      try {
        await axios.delete(
          `${API}/manager/groups/${selectedGroup.id}/members/${userId}?manager_id=${manager.id}`,
          axiosConfig
        );
        
        showAlert('Üye çıkarıldı!', 'success');
        handleOpenAddMember(selectedGroup); // Refresh members
        fetchData();
      } catch (error) {
        showAlert('Hata: ' + (error.response?.data?.detail || 'Hata oluştu!'), 'error');
      }
    });
  };

  const handleChangeAccountType = async (userId, newType) => {
    try {
      await axios.put(
        `${API}/manager/users/${userId}/account-type?manager_id=${manager.id}&account_type=${newType}`,
        {},
        axiosConfig
      );
      
      showAlert('Hesap tipi değiştirildi!', 'success');
      handleOpenAddMember(selectedGroup); // Refresh members
    } catch (error) {
      showAlert('Hata: ' + (error.response?.data?.detail || 'Hata oluştu!'), 'error');
    }
  };

  // Kullanıcı detayını API'den çek (Admin paneli ile aynı)
  const fetchUserDetail = async (userId) => {
    try {
      setUserDetailLoading(true);
      const userResponse = await axios.get(`${API}/admin/users/${userId}`, axiosConfig);
      setSelectedUserForDetail(userResponse.data);
      setShowUserDetailModal(true);
      setUserDetailLoading(false);
    } catch (err) {
      console.error('Error fetching user detail:', err);
      showAlert('Kullanıcı detayları alınamadı', 'error');
      setUserDetailLoading(false);
    }
  };

  const handleCloseUserDetail = () => {
    setShowUserDetailModal(false);
    setSelectedUserForDetail(null);
    fetchData(); // Refresh data
  };

  // =============================================
  // GRUP VARLIK AYARLARI
  // =============================================
  
  // Tüm varlıkları çek
  const fetchAllAssets = async () => {
    try {
      const response = await axios.get(`${API}/assets/minimal`, axiosConfig);
      // API returns {assets: [...], usd_try_rate: ...}
      const assetsData = response.data?.assets || response.data || [];
      setAllAssets(assetsData);
    } catch (err) {
      console.error('Varlıklar alınamadı:', err);
    }
  };

  // Grup varlık ayarlarını çek
  const fetchGroupAssetSettings = async (groupId) => {
    try {
      const response = await axios.get(`${API}/manager/groups/${groupId}/assets`, axiosConfig);
      setGroupAssetSettings(response.data || {});
    } catch (err) {
      console.error('Grup varlık ayarları alınamadı:', err);
      setGroupAssetSettings({});
    }
  };

  // Varlık ayarları modalını aç
  const handleOpenAssetSettings = async (group) => {
    setSelectedGroupForAssets(group);
    setShowAssetSettings(true);
    setAssetSearchQuery('');
    setEditingAsset(null);
    await fetchAllAssets();
    await fetchGroupAssetSettings(group.id);
  };

  // Varlık düzenleme modalını aç
  const handleEditAsset = (asset) => {
    const existingSetting = groupAssetSettings[asset.symbol] || {};
    setEditingAsset(asset);
    setAssetSettingForm({
      price_adjustment: existingSetting.price_adjustment || 0
    });
  };

  // Varlık ayarını kaydet
  const handleSaveAssetSetting = async () => {
    if (!editingAsset || !selectedGroupForAssets) return;
    
    try {
      await axios.put(
        `${API}/manager/groups/${selectedGroupForAssets.id}/assets/${editingAsset.symbol}`,
        assetSettingForm,
        axiosConfig
      );
      showAlert(`${editingAsset.symbol} ayarları kaydedildi!`, 'success');
      setEditingAsset(null);
      await fetchGroupAssetSettings(selectedGroupForAssets.id);
    } catch (err) {
      showAlert('Hata: Ayarlar kaydedilemedi: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  // Varlık ayarını sil
  const handleDeleteAssetSetting = async (symbol) => {
    if (!selectedGroupForAssets) return;
    
    try {
      await axios.delete(
        `${API}/manager/groups/${selectedGroupForAssets.id}/assets/${symbol}`,
        axiosConfig
      );
      showAlert(`${symbol} ayarları kaldırıldı!`, 'success');
      await fetchGroupAssetSettings(selectedGroupForAssets.id);
    } catch (err) {
      showAlert('Hata: Ayarlar kaldırılamadı', 'error');
    }
  };

  // Filtrelenmiş varlıklar
  const filteredAssets = allAssets.filter(asset => 
    asset.symbol?.toLowerCase().includes(assetSearchQuery.toLowerCase()) ||
    asset.name?.toLowerCase().includes(assetSearchQuery.toLowerCase())
  );

  const handleOpenEditMargin = (group) => {
    setEditingGroup(group);
    setNewMargin(group.price_margin);
    setShowEditMargin(true);
  };

  const handleUpdateMargin = async () => {
    try {
      await axios.put(
        `${API}/manager/groups/${editingGroup.id}?manager_id=${manager.id}`,
        { price_margin: newMargin },
        axiosConfig
      );
      
      showAlert('Fiyat marjı güncellendi!', 'success');
      setShowEditMargin(false);
      fetchData();
    } catch (error) {
      showAlert('Hata: ' + (error.response?.data?.detail || 'Hata oluştu!'), 'error');
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const availableUsers = allUsers.filter(u => !u.group_id);

  if (loading) {
    return <div className="min-h-screen bg-gray-900 flex items-center justify-center"><div className="text-white text-xl">Yükleniyor...</div></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4 sm:p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6 sm:mb-8">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-3xl font-bold text-white mb-2">👨‍💼 Manager Panel</h1>
            <p className="text-gray-400 text-sm sm:text-base truncate">Hoş geldin, {manager?.full_name}</p>
          </div>
          <button
            onClick={handleLogout}
            className="bg-red-600 hover:bg-red-700 text-white px-3 sm:px-6 py-2 sm:py-3 rounded-lg flex items-center gap-2 transition-colors text-sm sm:text-base ml-2"
          >
            <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Çıkış Yap</span>
            <span className="sm:hidden">Çıkış</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-blue-600/20 border border-blue-500/30 rounded-xl p-4 sm:p-6">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
            <div>
              <p className="text-gray-400 text-xs sm:text-sm">Toplam Grup</p>
              <p className="text-white text-2xl sm:text-3xl font-bold">{groups.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-green-600/20 border border-green-500/30 rounded-xl p-4 sm:p-6">
          <div className="flex items-center gap-3">
            <UserPlus className="w-6 h-6 sm:w-8 sm:h-8 text-green-400" />
            <div>
              <p className="text-gray-400 text-xs sm:text-sm">Toplam Üye</p>
              <p className="text-white text-2xl sm:text-3xl font-bold">{groups.reduce((sum, g) => sum + g.member_ids.length, 0)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-purple-600/20 border border-purple-500/30 rounded-xl p-4 sm:p-6 sm:col-span-2 lg:col-span-1">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400" />
            <div>
              <p className="text-gray-400 text-xs sm:text-sm">Müsait Kullanıcı</p>
              <p className="text-white text-2xl sm:text-3xl font-bold">{availableUsers.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex gap-1 sm:gap-2 bg-gray-800 rounded-lg p-1 sm:p-2 border border-gray-700">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex-1 px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-all text-sm sm:text-base ${
              activeTab === 'users'
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <UserCog className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Kullanıcılar</span>
            <span className="sm:hidden">Kullanıcı</span>
          </button>
          <button
            onClick={() => setActiveTab('groups')}
            className={`flex-1 px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-all text-sm sm:text-base ${
              activeTab === 'groups'
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <Users className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Gruplarım</span>
            <span className="sm:hidden">Grup</span>
          </button>
        </div>
      </div>

      {/* Users List */}
      {activeTab === 'users' && (
        <div className="max-w-7xl mx-auto">
          <div className="bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-2">
              <h2 className="text-white font-bold text-xl sm:text-2xl">👥 Tüm Kullanıcılar</h2>
              <div className="text-gray-400 text-sm">
                Toplam {allUsers.length} kullanıcı
              </div>
            </div>

            {allUsers.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400 text-lg">Henüz kullanıcı yok</p>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">Kullanıcı</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">Email</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">Bakiye</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">Hesap Tipi</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allUsers.map(user => (
                        <tr key={user.id} className="border-b border-gray-700 hover:bg-gray-700/50">
                          <td className="py-3 px-4 text-white">{user.full_name || 'N/A'}</td>
                          <td className="py-3 px-4 text-gray-400">{user.email}</td>
                          <td className="py-3 px-4 text-green-400 font-bold">
                            ₺{user.balance?.toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              user.account_type === 'VIP' ? 'bg-yellow-900 text-yellow-300' :
                              user.account_type === 'KOMISYONSUZ' ? 'bg-blue-900 text-blue-300' :
                              'bg-gray-700 text-gray-300'
                            }`}>
                              {user.account_type || 'STANDART'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => fetchUserDetail(user.id)}
                              disabled={userDetailLoading}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                            >
                              {userDetailLoading ? '' : '📊'} Detay & Yönet
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-3">
                  {allUsers.map(user => (
                    <div key={user.id} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                      <div className="flex flex-col space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-semibold truncate">{user.full_name || 'N/A'}</h3>
                            <p className="text-gray-400 text-sm truncate">{user.email}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ml-2 ${
                            user.account_type === 'VIP' ? 'bg-yellow-900 text-yellow-300' :
                            user.account_type === 'KOMISYONSUZ' ? 'bg-blue-900 text-blue-300' :
                            'bg-gray-600 text-gray-300'
                          }`}>
                            {user.account_type || 'STANDART'}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="text-green-400 font-bold">
                            ₺{user.balance?.toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                          </div>
                          <button
                            onClick={() => fetchUserDetail(user.id)}
                            disabled={userDetailLoading}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                          >
                            {userDetailLoading ? '' : '📊'} Yönet
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Groups List */}
      {activeTab === 'groups' && (
        <div className="max-w-7xl mx-auto">
          <div className="bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3">
              <h2 className="text-white font-bold text-xl sm:text-2xl">Gruplarım</h2>
              <button
                onClick={() => setShowCreateGroup(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm sm:text-base"
              >
                <UserPlus className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Yeni Grup Oluştur</span>
                <span className="sm:hidden">Yeni Grup</span>
              </button>
            </div>

          {groups.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 text-base sm:text-lg">Henüz grup oluşturmadınız</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {groups.map(group => (
                <div key={group.id} className="bg-gray-700 rounded-lg p-4 sm:p-6 border border-gray-600 hover:border-blue-500 transition-colors">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-bold text-base sm:text-lg truncate pr-2">{group.name}</h3>
                    <button
                      onClick={() => handleDeleteGroup(group.id)}
                      className="text-red-400 hover:text-red-300 flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs sm:text-sm">Üye Sayısı:</span>
                      <span className="text-white font-bold text-sm sm:text-base">{group.member_ids.length}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs sm:text-sm">Fiyat Marjı:</span>
                      <span className={`font-bold text-sm sm:text-base ${group.price_margin > 0 ? 'text-red-400' : group.price_margin < 0 ? 'text-green-400' : 'text-gray-400'}`}>
                        {group.price_margin > 0 ? '+' : ''}{group.price_margin}%
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 mt-4">
                    <div className="flex flex-row gap-2">
                      <button
                        onClick={() => handleOpenEditMargin(group)}
                        className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
                      >
                        <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">Fiyat Marjı</span>
                        <span className="sm:hidden">Marj</span>
                      </button>
                      <button
                        onClick={() => handleOpenAddMember(group)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg transition-colors text-sm sm:text-base"
                      >
                        <span className="hidden sm:inline">Üyeleri Yönet</span>
                        <span className="sm:hidden">Üyeler</span>
                      </button>
                    </div>
                    <button
                      onClick={() => handleOpenAssetSettings(group)}
                      className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white py-2 rounded-lg transition-all flex items-center justify-center gap-2 text-sm sm:text-base font-semibold"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Varlık Ayarları</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Create Group Modal */}
      {showCreateGroup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-4 sm:p-8 w-full max-w-md border border-gray-700">
            <h2 className="text-white font-bold text-xl sm:text-2xl mb-4 sm:mb-6">Yeni Grup Oluştur</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2 text-sm sm:text-base">Grup Adı</label>
                <input
                  type="text"
                  value={newGroup.name}
                  onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                  className="w-full bg-gray-700 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg outline-none text-sm sm:text-base"
                  placeholder="VIP Müşterilerim"
                />
              </div>
              
              <div>
                <label className="block text-gray-300 mb-2 text-sm sm:text-base">Fiyat Marjı (%)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="1"
                    value={newGroup.price_margin}
                    onChange={(e) => setNewGroup({ ...newGroup, price_margin: parseFloat(e.target.value) || 0 })}
                    className="flex-1 bg-gray-700 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-center font-bold outline-none text-sm sm:text-base"
                    placeholder="0"
                  />
                </div>
                <p className="text-gray-400 text-xs sm:text-sm mt-2">
                  {newGroup.price_margin > 0 ? 'Fiyatlar artırılacak' : newGroup.price_margin < 0 ? 'İndirim uygulanacak' : 'Fiyat değişmeyecek'} • Sınırsız değer girebilirsiniz
                </p>
              </div>
            </div>

            <div className="flex gap-2 sm:gap-3 mt-4 sm:mt-6">
              <button
                onClick={() => setShowCreateGroup(false)}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 sm:py-3 rounded-lg transition-colors text-sm sm:text-base"
              >
                İptal
              </button>
              <button
                onClick={handleCreateGroup}
                disabled={!newGroup.name}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 sm:py-3 rounded-lg transition-colors disabled:opacity-50 text-sm sm:text-base"
              >
                Oluştur
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMember && selectedGroup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl p-4 sm:p-8 w-full max-w-4xl border border-gray-700 my-4 sm:my-8">
            <h2 className="text-white font-bold text-lg sm:text-2xl mb-2 truncate">{selectedGroup.name}</h2>
            <p className="text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">Fiyat Marjı: {selectedGroup.price_margin > 0 ? '+' : ''}{selectedGroup.price_margin}%</p>
            
            {/* Current Members */}
            <div className="mb-6 sm:mb-8">
              <h3 className="text-white font-bold text-base sm:text-lg mb-3 sm:mb-4">Grup Üyeleri ({groupMembers.length})</h3>
              {groupMembers.length === 0 ? (
                <p className="text-gray-400 text-sm sm:text-base">Henüz üye yok</p>
              ) : (
                <div className="space-y-2 max-h-48 sm:max-h-60 overflow-y-auto">
                  {groupMembers.map(member => (
                    <div key={member.id} className="bg-gray-700 rounded-lg p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium text-sm sm:text-base truncate">{member.full_name || member.email}</p>
                          <p className="text-gray-400 text-xs sm:text-sm truncate">{member.email}</p>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
                          <select
                            value={member.account_type}
                            onChange={(e) => handleChangeAccountType(member.id, e.target.value)}
                            className="bg-gray-600 text-white px-2 sm:px-3 py-1 sm:py-2 rounded-lg text-xs sm:text-sm"
                          >
                            <option value="STANDART">STANDART</option>
                            <option value="VIP">VIP</option>
                            <option value="KOMISYONSUZ">KOMISYONSUZ</option>
                          </select>
                          <button
                            onClick={() => fetchUserDetail(member.id)}
                            disabled={userDetailLoading}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-2 sm:px-3 py-1 sm:py-2 rounded-lg text-xs sm:text-sm whitespace-nowrap disabled:opacity-50"
                          >
                            {userDetailLoading ? '' : '📊'} <span className="hidden sm:inline">Detay & </span>Yönet
                          </button>
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            className="text-red-400 hover:text-red-300 flex-shrink-0"
                          >
                            <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Available Users */}
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <h3 className="text-white font-bold text-base sm:text-lg">Müsait Kullanıcılar ({availableUsers.filter(u =>
                  !memberSearchQuery ||
                  u.email?.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
                  u.full_name?.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
                  u.id?.toString().includes(memberSearchQuery)
                ).length})</h3>
                <input
                  type="text"
                  placeholder="🔍 İsim, email veya ID ara..."
                  value={memberSearchQuery}
                  onChange={(e) => setMemberSearchQuery(e.target.value)}
                  className="w-full sm:w-64 px-3 py-2 bg-gray-700 text-white rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {availableUsers.length === 0 ? (
                <p className="text-gray-400 text-sm sm:text-base">Tüm kullanıcılar bir gruba ait</p>
              ) : (
                <div className="space-y-2 max-h-48 sm:max-h-60 overflow-y-auto">
                  {availableUsers
                    .filter(u =>
                      !memberSearchQuery ||
                      u.email?.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
                      u.full_name?.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
                      u.id?.toString().includes(memberSearchQuery)
                    )
                    .map(user => (
                    <div key={user.id} className="bg-gray-700 rounded-lg p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium text-sm sm:text-base truncate">{user.full_name || user.email}</p>
                          <p className="text-gray-400 text-xs sm:text-sm truncate">{user.email} • {user.account_type}</p>
                        </div>
                        <button
                          onClick={() => handleAddMember(user.id)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm transition-colors"
                        >
                          <span className="hidden sm:inline">Gruba Ekle</span>
                          <span className="sm:hidden">Ekle</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => {
                setShowAddMember(false);
                setMemberSearchQuery('');
              }}
              className="w-full mt-4 sm:mt-6 bg-gray-700 hover:bg-gray-600 text-white py-2 sm:py-3 rounded-lg transition-colors text-sm sm:text-base"
            >
              Kapat
            </button>
          </div>
        </div>
      )}

      {/* Edit Price Margin Modal */}
      {showEditMargin && editingGroup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-4 sm:p-8 w-full max-w-md border border-gray-700 max-h-screen overflow-y-auto">
            <h2 className="text-white font-bold text-lg sm:text-2xl mb-2 truncate">{editingGroup.name}</h2>
            <p className="text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">Grup Fiyat Marjını Düzenle</p>
            
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
              <div className="text-blue-300 text-xs sm:text-sm">
                <strong>Bilgi:</strong>
                <p className="mt-2 text-gray-300">
                  Fiyat marjı bu gruptaki tüm üyeler için geçerlidir. 
                  Pozitif değer fiyatları artırır, negatif değer indirim uygular.
                </p>
              </div>
            </div>
            
            <div>
              <label className="block text-gray-300 mb-2 text-sm sm:text-base">Fiyat Marjı (%)</label>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="number"
                  step="1"
                  value={newMargin}
                  onChange={(e) => setNewMargin(parseFloat(e.target.value) || 0)}
                  className="flex-1 bg-gray-700 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-center font-bold text-lg sm:text-xl outline-none"
                  placeholder="0"
                />
              </div>
              <p className="text-gray-400 text-xs sm:text-sm text-center">
                ♾️ Sınırsız değer girebilirsiniz (örn: -500, +1000)
              </p>
              
              <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-700 rounded-lg">
                <p className="text-gray-400 text-xs sm:text-sm mb-2">Örnek Hesaplama:</p>
                <div className="space-y-1 text-xs sm:text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-300">Orijinal Fiyat:</span>
                    <span className="text-white font-bold">100 TL</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Margin ({newMargin > 0 ? '+' : ''}{newMargin}%):</span>
                    <span className={newMargin > 0 ? 'text-red-400' : newMargin < 0 ? 'text-green-400' : 'text-gray-400'}>
                      {newMargin > 0 ? '+' : ''}{newMargin} TL
                    </span>
                  </div>
                  <div className="border-t border-gray-600 pt-1 mt-1 flex justify-between">
                    <span className="text-white font-bold">Kullanıcı Görür:</span>
                    <span className="text-white font-bold">{(100 * (1 + newMargin / 100)).toFixed(2)} TL</span>
                  </div>
                </div>
              </div>
              
              <p className={`text-xs sm:text-sm mt-3 ${newMargin > 0 ? 'text-red-400' : newMargin < 0 ? 'text-green-400' : 'text-gray-400'}`}>
                {newMargin > 0 ? 'Fiyatlar artırılacak' : newMargin < 0 ? 'İndirim uygulanacak' : 'Fiyat değişmeyecek'}
              </p>
            </div>

            <div className="flex gap-2 sm:gap-3 mt-4 sm:mt-6">
              <button
                onClick={() => setShowEditMargin(false)}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 sm:py-3 rounded-lg transition-colors text-sm sm:text-base"
              >
                İptal
              </button>
              <button
                onClick={handleUpdateMargin}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2 sm:py-3 rounded-lg transition-colors text-sm sm:text-base"
              >
                Güncelle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Varlık Ayarları Modal */}
      {showAssetSettings && selectedGroupForAssets && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl w-full max-w-5xl border border-gray-700 my-4 sm:my-8 max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-4 sm:p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-white font-bold text-lg sm:text-2xl flex items-center gap-2">
                    Varlık Ayarları
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Grup: <span className="text-orange-400 font-semibold">{selectedGroupForAssets.name}</span>
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowAssetSettings(false);
                    setEditingAsset(null);
                  }}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  ✕
                </button>
              </div>
              
              {/* Bilgi Kutusu */}
              <div className="mt-4 bg-blue-900/30 border border-blue-500/30 rounded-lg p-3 text-sm">
                <p className="text-blue-300">
                  💡 <strong>Bu ayarlar sadece bu gruptaki kullanıcılara uygulanır.</strong>
                </p>
                <ul className="text-gray-300 mt-2 space-y-1 text-xs sm:text-sm">
                  <li>• <strong>Fiyat Ayarı:</strong> Varlığın fiyatına % olarak eklenir/çıkarılır</li>
                  <li>• <strong>Yüzde Değişim:</strong> Gösterilen değişim yüzdesine eklenir/çıkarılır</li>
                  <li>• <strong>Gizli Kaldıraç:</strong> K/Z hesaplamasında çarpan olarak kullanılır</li>
                </ul>
              </div>
            </div>

            {/* Arama */}
            <div className="p-4 border-b border-gray-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Varlık ara (sembol veya isim)..."
                  value={assetSearchQuery}
                  onChange={(e) => setAssetSearchQuery(e.target.value)}
                  className="w-full bg-gray-700 text-white pl-10 pr-4 py-3 rounded-lg outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div className="flex items-center gap-4 mt-3 text-sm">
                <span className="text-gray-400">
                  Toplam: <span className="text-white font-bold">{allAssets.length}</span> varlık
                </span>
                <span className="text-gray-400">
                  Özelleştirilmiş: <span className="text-orange-400 font-bold">{Object.keys(groupAssetSettings).length}</span>
                </span>
              </div>
            </div>

            {/* İçerik */}
            <div className="flex-1 overflow-y-auto p-4">
              {editingAsset ? (
                // Düzenleme Formu
                <div className="bg-gray-700 rounded-lg p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-bold text-lg flex items-center gap-2">
                      <span className="bg-blue-600 px-2 py-1 rounded text-sm">{editingAsset.symbol}</span>
                      <span className="text-gray-300 text-sm font-normal">{editingAsset.name}</span>
                    </h3>
                    <button
                      onClick={() => setEditingAsset(null)}
                      className="text-gray-400 hover:text-white"
                    >
                      ← Geri
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Fiyat Ayarı */}
                    <div className="bg-gray-800 rounded-lg p-4">
                      <label className="block text-gray-300 mb-2 text-sm font-semibold">
                        Fiyat Ayarı (%)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={assetSettingForm.price_adjustment}
                        onChange={(e) => setAssetSettingForm({
                          ...assetSettingForm,
                          price_adjustment: parseFloat(e.target.value) || 0
                        })}
                        className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg text-center font-bold text-lg outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      <p className={`text-xs mt-2 text-center ${
                        assetSettingForm.price_adjustment > 0 ? 'text-red-400' :
                        assetSettingForm.price_adjustment < 0 ? 'text-green-400' : 'text-gray-500'
                      }`}>
                        {assetSettingForm.price_adjustment > 0 ? '↑ Fiyat artırılacak' :
                         assetSettingForm.price_adjustment < 0 ? '↓ Fiyat düşürülecek' : 'Değişiklik yok'}
                      </p>
                    </div>

                    {/* Faz 2 Paket 3 — Yüzde Değişim Ayarı ve Gizli Kaldıraç
                        alanları KALDIRILDI. Yüzde değişimi yalnız gerçek fiyat
                        hareketiyle, kaldıraç yalnız grup tablosuyla belirlenir. */}
                  </div>

                  {/* Örnek Hesaplama */}
                  <div className="mt-4 bg-gray-900/50 rounded-lg p-4">
                    <h4 className="text-gray-400 text-sm mb-3">Örnek Hesaplama (100₺ fiyatlı varlık için)</h4>
                    <div className="grid grid-cols-1 gap-4 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Görünen Fiyat:</span>
                        <span className="text-white font-bold">
                          {(100 * (1 + assetSettingForm.price_adjustment / 100)).toFixed(2)}₺
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Kaydet Butonları */}
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setEditingAsset(null)}
                      className="flex-1 bg-gray-600 hover:bg-gray-500 text-white py-3 rounded-lg font-semibold transition-colors"
                    >
                      İptal
                    </button>
                    <button
                      onClick={handleSaveAssetSetting}
                      className="flex-1 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white py-3 rounded-lg font-semibold transition-all"
                    >
                      ✓ Kaydet
                    </button>
                  </div>
                </div>
              ) : (
                // Varlık Listesi
                <div className="space-y-2">
                  {/* Özelleştirilmiş Varlıklar */}
                  {Object.keys(groupAssetSettings).length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-orange-400 font-bold mb-3 flex items-center gap-2">
                        Özelleştirilmiş Varlıklar ({Object.keys(groupAssetSettings).length})
                      </h3>
                      <div className="space-y-2">
                        {Object.entries(groupAssetSettings).map(([symbol, setting]) => {
                          const asset = allAssets.find(a => a.symbol === symbol);
                          return (
                            <div key={symbol} className="bg-gradient-to-r from-orange-900/30 to-amber-900/30 border border-orange-500/30 rounded-lg p-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="bg-orange-600 px-2 py-1 rounded text-white font-bold text-sm">{symbol}</span>
                                <span className="text-gray-300 text-sm">{asset?.name || symbol}</span>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="flex gap-3 text-xs">
                                  {setting.price_adjustment !== 0 && (
                                    <span className={setting.price_adjustment > 0 ? 'text-red-400' : 'text-green-400'}>
                                      {setting.price_adjustment > 0 ? '+' : ''}{setting.price_adjustment}%
                                    </span>
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleEditAsset(asset || { symbol, name: symbol })}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    onClick={() => handleDeleteAssetSetting(symbol)}
                                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Tüm Varlıklar */}
                  <h3 className="text-gray-400 font-bold mb-3">
                    Tüm Varlıklar ({filteredAssets.length})
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {filteredAssets.slice(0, 100).map(asset => {
                      const hasCustomSetting = groupAssetSettings[asset.symbol];
                      return (
                        <div
                          key={asset.id || asset.symbol}
                          onClick={() => handleEditAsset(asset)}
                          className={`bg-gray-700 hover:bg-gray-600 rounded-lg p-3 cursor-pointer transition-colors border ${
                            hasCustomSetting ? 'border-orange-500/50' : 'border-transparent'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                hasCustomSetting ? 'bg-orange-600 text-white' : 'bg-gray-600 text-gray-300'
                              }`}>
                                {asset.symbol}
                              </span>
                              <span className="text-gray-400 text-xs truncate">{asset.name}</span>
                            </div>
                            <div className="text-right ml-2">
                              <div className="text-white text-sm font-mono">
                                {asset.current_price?.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </div>
                              <div className={`text-xs ${
                                (asset.change_percent || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                              }`}>
                                {(asset.change_percent || 0) >= 0 ? '+' : ''}{(asset.change_percent || 0).toFixed(2)}%
                              </div>
                            </div>
                          </div>
                          {hasCustomSetting && (
                            <div className="mt-2 flex gap-2 text-xs">
                              {hasCustomSetting.price_adjustment !== 0 && (
                                <span className="text-orange-400">💰{hasCustomSetting.price_adjustment > 0 ? '+' : ''}{hasCustomSetting.price_adjustment}%</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {filteredAssets.length > 100 && (
                    <p className="text-gray-500 text-center text-sm mt-4">
                      ... ve {filteredAssets.length - 100} varlık daha. Arama yaparak daraltın.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-700">
              <button
                onClick={() => {
                  setShowAssetSettings(false);
                  setEditingAsset(null);
                }}
                className="w-full bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-lg font-semibold transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Detail Management Modal */}
      {showUserDetailModal && selectedUserForDetail && (
        <UserDetailManagement
          user={selectedUserForDetail}
          onClose={handleCloseUserDetail}
          onRefresh={fetchData}
        />
      )}

      {/* Site İçi Alert Modal */}
      {alertModal.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">
                {alertModal.type === 'success' ? '' : alertModal.type === 'error' ? '' : ''}
              </div>
              <p className={`text-sm mb-4 whitespace-pre-line ${alertModal.type === 'success' ? 'text-green-400' : alertModal.type === 'error' ? 'text-red-400' : 'text-gray-300'}`}>
                {alertModal.message}
              </p>
              <button
                onClick={() => setAlertModal({ show: false, message: '', type: 'info' })}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Site İçi Confirm Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3"></div>
              <p className="text-sm mb-4 text-gray-300">{confirmModal.message}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
                  className="flex-1 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={() => {
                    if (confirmModal.onConfirm) confirmModal.onConfirm();
                    setConfirmModal({ show: false, message: '', onConfirm: null });
                  }}
                  className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Evet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManagerPanel;
